import api from './api';
import { nowCv } from '../utils/date';

// ============================================
// TYPES
// ============================================

export type PromotionType = 'percentual' | 'valor_fixo' | 'frete_gratis' | 'compre_ganhe';
export type CouponType = 'percentual' | 'valor_fixo' | 'frete_gratis';

export interface Promotion {
  id: number;
  storeId: number;
  storeName?: string;
  name: string;
  description?: string;
  type: PromotionType;
  value: number;
  minValue: number;
  maxDiscount?: number | null;
  startDate: string;
  endDate: string;
  active: boolean;
  applicableProducts?: number[] | null;
  applicableCategories?: number[] | null;
  maxUsage?: number | null;
  currentUsage: number;
  createdAt: string;
  updatedAt: string;
}

export interface Coupon {
  id: number;
  storeId?: number | null;
  storeName?: string;
  code: string;
  name: string;
  description?: string;
  type: CouponType;
  value: number;
  minValue: number;
  maxDiscount?: number | null;
  startDate: string;
  endDate: string;
  active: boolean;
  maxUsage?: number | null;
  usagePerCustomer: number;
  currentUsage: number;
  firstPurchaseOnly: boolean;
  applicableProducts?: number[] | null;
  applicableCategories?: number[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface CouponStats {
  totalUsage: number;
  totalDiscount: number;
  avgDiscount: number;
  firstUsage?: string;
  lastUsage?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface PromotionFilters {
  page?: number;
  limit?: number;
  storeId?: number;
  active?: boolean;
  search?: string;
}

export interface CouponFilters {
  page?: number;
  limit?: number;
  storeId?: number;
  active?: boolean;
  search?: string;
}

export interface CreatePromotionData {
  storeId: number;
  name: string;
  description?: string;
  type?: PromotionType;
  value?: number;
  minValue?: number;
  maxDiscount?: number | null;
  startDate: string;
  endDate: string;
  active?: boolean;
  applicableProducts?: number[];
  applicableCategories?: number[];
  maxUsage?: number | null;
}

export interface CreateCouponData {
  storeId?: number | null;
  code: string;
  name: string;
  description?: string;
  type?: CouponType;
  value?: number;
  minValue?: number;
  maxDiscount?: number | null;
  startDate: string;
  endDate: string;
  active?: boolean;
  maxUsage?: number | null;
  usagePerCustomer?: number;
  firstPurchaseOnly?: boolean;
  applicableProducts?: number[];
  applicableCategories?: number[];
}

// ============================================
// SERVICE
// ============================================

class PromotionsService {
  // ============================================
  // SETUP
  // ============================================

  async setupTables(): Promise<{
    success: boolean;
    message: string;
    created: string[];
  }> {
    try {
      const response = await api.post('/admin/shop/promotions/setup');
      return response.data;
    } catch (error) {
      console.error('Setup promotions tables error:', error);
      throw error;
    }
  }

  // ============================================
  // PROMOTIONS
  // ============================================

  async getPromotions(params?: PromotionFilters): Promise<PaginatedResponse<Promotion>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.active !== undefined) queryParams.append('active', String(params.active));
      if (params?.search) queryParams.append('search', params.search);

      const response = await api.get(`/admin/shop/promotions?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get promotions error:', error);
      throw error;
    }
  }

  async getPromotion(id: number): Promise<{ success: boolean; data: Promotion }> {
    try {
      const response = await api.get(`/admin/shop/promotions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get promotion error:', error);
      throw error;
    }
  }

  async createPromotion(data: CreatePromotionData): Promise<{
    success: boolean;
    message: string;
    data: { id: number };
  }> {
    try {
      const response = await api.post('/admin/shop/promotions', data);
      return response.data;
    } catch (error) {
      console.error('Create promotion error:', error);
      throw error;
    }
  }

  async updatePromotion(id: number, data: Partial<CreatePromotionData>): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.put(`/admin/shop/promotions/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update promotion error:', error);
      throw error;
    }
  }

  async deletePromotion(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/promotions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete promotion error:', error);
      throw error;
    }
  }

  // ============================================
  // COUPONS
  // ============================================

  async getCoupons(params?: CouponFilters): Promise<PaginatedResponse<Coupon>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.active !== undefined) queryParams.append('active', String(params.active));
      if (params?.search) queryParams.append('search', params.search);

      const response = await api.get(`/admin/shop/promotions/coupons/list?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get coupons error:', error);
      throw error;
    }
  }

  async getCoupon(id: number): Promise<{ success: boolean; data: Coupon }> {
    try {
      const response = await api.get(`/admin/shop/promotions/coupons/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get coupon error:', error);
      throw error;
    }
  }

  async getCouponByCode(code: string, storeId?: number): Promise<{ success: boolean; data: Coupon }> {
    try {
      const queryParams = storeId ? `?storeId=${storeId}` : '';
      const response = await api.get(`/admin/shop/promotions/coupons/code/${code}${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Get coupon by code error:', error);
      throw error;
    }
  }

  async createCoupon(data: CreateCouponData): Promise<{
    success: boolean;
    message: string;
    data: { id: number; code: string };
  }> {
    try {
      const response = await api.post('/admin/shop/promotions/coupons', data);
      return response.data;
    } catch (error) {
      console.error('Create coupon error:', error);
      throw error;
    }
  }

  async updateCoupon(id: number, data: Partial<CreateCouponData>): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.put(`/admin/shop/promotions/coupons/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update coupon error:', error);
      throw error;
    }
  }

  async deleteCoupon(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/promotions/coupons/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete coupon error:', error);
      throw error;
    }
  }

  async applyCoupon(couponId: number, orderId: number, clientId?: number, discountValue?: number): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.post(`/admin/shop/promotions/coupons/${couponId}/apply`, {
        orderId,
        clientId,
        discountValue,
      });
      return response.data;
    } catch (error) {
      console.error('Apply coupon error:', error);
      throw error;
    }
  }

  async getCouponStats(id: number): Promise<{ success: boolean; data: CouponStats }> {
    try {
      const response = await api.get(`/admin/shop/promotions/coupons/${id}/stats`);
      return response.data;
    } catch (error) {
      console.error('Get coupon stats error:', error);
      throw error;
    }
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  getTypeLabel(type: PromotionType | CouponType): string {
    const labels: Record<string, string> = {
      percentual: 'Percentual',
      valor_fixo: 'Valor Fixo',
      frete_gratis: 'Frete Grátis',
      compre_ganhe: 'Compre e Ganhe',
    };
    return labels[type] || type;
  }

  getTypeColor(type: PromotionType | CouponType): 'primary' | 'secondary' | 'success' | 'info' {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'info'> = {
      percentual: 'primary',
      valor_fixo: 'secondary',
      frete_gratis: 'success',
      compre_ganhe: 'info',
    };
    return colors[type] || 'primary';
  }

  formatDiscount(type: PromotionType | CouponType, value: number): string {
    if (type === 'percentual') {
      return `${value}%`;
    } else if (type === 'valor_fixo') {
      return this.formatCurrency(value);
    } else if (type === 'frete_gratis') {
      return 'Frete Grátis';
    }
    return String(value);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
    }).format(value);
  }

  // Datas de promoção são hora de parede de CV marcada com Z → comparar com «agora em CV».
  // endDate só-dia (00:00Z) vale até ao fim desse dia.
  private janela(promotion: Promotion | Coupon): { now: Date; start: Date; end: Date } {
    const now = nowCv();
    const start = new Date(promotion.startDate);
    const end = new Date(promotion.endDate);
    if (end.getUTCHours() === 0 && end.getUTCMinutes() === 0 && end.getUTCSeconds() === 0) {
      end.setTime(end.getTime() + 86400e3 - 1);
    }
    return { now, start, end };
  }

  isPromotionActive(promotion: Promotion | Coupon): boolean {
    if (!promotion.active) return false;
    const { now, start, end } = this.janela(promotion);
    return now >= start && now <= end;
  }

  getStatusLabel(promotion: Promotion | Coupon): string {
    if (!promotion.active) return 'Inativo';
    const { now, start, end } = this.janela(promotion);
    if (now < start) return 'Agendado';
    if (now > end) return 'Expirado';
    return 'Ativo';
  }

  getStatusColor(promotion: Promotion | Coupon): 'success' | 'warning' | 'error' | 'default' {
    if (!promotion.active) return 'default';
    const { now, start, end } = this.janela(promotion);
    if (now < start) return 'warning';
    if (now > end) return 'error';
    return 'success';
  }

  generateCouponCode(length: number = 8): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }
}

export default new PromotionsService();
